import { useState } from 'react';
import { Search, Zap } from 'lucide-react';
import { LoadingState } from './LoadingState';
import { ResultsSection } from './ResultsSection';

const MOCK_HOTELS = [
  { id: 1, name: 'Mercure Lyon', location: 'Paris, France', stars: 4 },
  { id: 2, name: 'Grand Hotel Paris', location: 'Paris, France', stars: 5 },
  { id: 3, name: 'The Ritz London', location: 'London, UK', stars: 5 },
  { id: 4, name: 'Hotel Marini Barcelona', location: 'Barcelona, Spain', stars: 4 },
];

const OTA_OPTIONS = [
  { id: 'booking', name: 'Booking.com' },
  { id: 'expedia', name: 'Expedia' },
  { id: 'agoda', name: 'Agoda' },
  { id: 'hotels', name: 'Hotels.com' },
];

export const AnalyzerSection = () => {
  const [hotelSearch, setHotelSearch] = useState('');
  const [selectedHotel, setSelectedHotel] = useState<typeof MOCK_HOTELS[0] | null>(null);
  const [selectedOTAs, setSelectedOTAs] = useState<string[]>(['booking', 'expedia', 'agoda', 'hotels']);
  const [customUrl, setCustomUrl] = useState('');
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [showResults, setShowResults] = useState(false);

  const filteredHotels = MOCK_HOTELS.filter(
    (hotel) =>
      hotel.name.toLowerCase().includes(hotelSearch.toLowerCase()) ||
      hotel.location.toLowerCase().includes(hotelSearch.toLowerCase())
  );

  const handleHotelSelect = (hotel: typeof MOCK_HOTELS[0]) => {
    setSelectedHotel(hotel);
    setHotelSearch(hotel.name);
    setShowSuggestions(false);
  };

  const handleOTAToggle = (otaId: string) => {
    setSelectedOTAs((prev) =>
      prev.includes(otaId) ? prev.filter((id) => id !== otaId) : [...prev, otaId]
    );
  };

  const canAnalyze = selectedHotel && selectedOTAs.length > 0;

  const handleAnalyze = () => {
    if (!canAnalyze) return;
    setIsAnalyzing(true);
    setShowResults(false);
  };

  const handleAnalysisComplete = () => {
    setIsAnalyzing(false);
    setShowResults(true);
  };

  if (showResults) {
    return (
      <ResultsSection
        hotelName={selectedHotel?.name || ''}
        hotelLocation={selectedHotel?.location || ''}
        hotelStars={selectedHotel?.stars || 4}
        selectedOTAs={selectedOTAs}
      />
    );
  }

  if (isAnalyzing) {
    return (
      <LoadingState
        hotelName={selectedHotel?.name || ''}
        hotelLocation={selectedHotel?.location || ''}
        hotelStars={selectedHotel?.stars || 4}
        selectedOTAs={selectedOTAs}
        onComplete={handleAnalysisComplete}
      />
    );
  }

  return (
    <section className="ha-min-h-screen ha-bg-background  ha-py-16 ha-relative ha-overflow-hidden">
      {/* Subtle Background Effects */}
      <div className="ha-absolute ha-inset-0 ha-opacity-20">
        <div className="ha-absolute ha-top-20 ha-left-20 ha-w-96 ha-h-96 ha-bg-primary ha-rounded-full ha-blur-[120px] ha-animate-pulse" style={{ animationDuration: '3s' }} />
        <div className="ha-absolute ha-bottom-20 ha-right-20 ha-w-96 ha-h-96 ha-bg-primary ha-rounded-full ha-blur-[120px] ha-animate-pulse" style={{ animationDelay: '1.5s', animationDuration: '3s' }} />
      </div>

      <div className="ha-container ha-mx-auto ha-px-4 ha-relative ha-z-10">
        <div className="ha-max-w-6xl ha-mx-auto">
          {/* Modern Clean Card */}
           <div className="ha-glass-card-enhanced ha-relative ha-overflow-hidden">

            {/* Modern 3-Column Layout with proper spacing */}
            <div className="ha-grid ha-grid-cols-1 ha-lg:grid-cols-3 ha-gap-12 ha-items-start">
              
              {/* LEFT COLUMN - Configuration */}
              <div className="ha-space-y-6">
                <h4 className="ha-text-black ha-text-xl ha-font-bold ha-mb-6 ha-text-center ha-lg:text-left">Configuration</h4>
                
                {/* Hotel Selection */}
                <div>
                  <label className="ha-block ha-text-black ha-text-sm ha-font-semibold ha-mb-2">
                    Sélectionnez votre hôtel
                  </label>
                  <div className="ha-relative">
                    <input
                      type="text"
                      value={hotelSearch}
                      onChange={(e) => {
                        setHotelSearch(e.target.value);
                        setShowSuggestions(true);
                        setSelectedHotel(null);
                      }}
                      onFocus={() => setShowSuggestions(true)}
                      placeholder="Tapez le nom de votre hôtel..."
                      className="ha-w-full ha-px-4 ha-py-3 ha-rounded-xl ha-border-2 ha-border-primary ha-text-black ha-placeholder:text-gray-500 focus-visible:ha-outline-none focus-visible:ha-border-primary focus-visible:ha-bg-white/20 ha-transition-all"
                      style={{ 
                        background: 'rgba(255, 255, 255, 0.1)',
                        backdropFilter: 'blur(10px)'
                      }}
                    />
                    {showSuggestions && hotelSearch && (
                      <div 
                        className="ha-absolute ha-z-50 ha-w-full ha-mt-2 ha-border-2 ha-border-primary ha-rounded-xl ha-overflow-hidden ha-shadow-xl ha-max-h-64 ha-overflow-y-auto"
                        style={{
                          background: 'rgba(17, 24, 39, 0.95)',
                          backdropFilter: 'blur(20px)'
                        }}
                      >
                        {filteredHotels.length > 0 ? (
                          filteredHotels.map((hotel) => (
                            <button
                              key={hotel.id}
                              onClick={() => handleHotelSelect(hotel)}
                              className="ha-w-full ha-px-4 ha-py-3 ha-text-left ha-transition-colors ha-border-b ha-border-primary/30 last:ha-border-b-0"
                              style={{
                                background: 'white'
                              }}
                              onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(255, 255, 255, 0.43)'}
                              onMouseLeave={(e) => e.currentTarget.style.background = 'white'}
                            >
                              <div className="ha-text-black ha-font-semibold">{hotel.name}</div>
                              <div className="ha-text-gray-400 ha-text-sm">{hotel.location}</div>
                            </button>
                          ))
                        ) : (
                          <div className="ha-px-4 ha-py-3 ha-text-gray-400">
                            Aucun hôtel trouvé
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>

                {/* OTA Selection with proper spacing */}
                <div>
                  <label className="ha-block ha-text-black ha-text-sm ha-font-semibold ha-mb-3">
                    Sélectionnez les OTA
                  </label>
                  <div className="ha-space-y-2">
                    {OTA_OPTIONS.map((ota) => {
                      const isSelected = selectedOTAs.includes(ota.id);
                      return (
                        <label
                          key={ota.id}
                          className="ha-flex ha-items-center ha-gap-3 ha-p-3 ha-rounded-lg ha-cursor-pointer ha-transition-all"
                          style={{
                            background: isSelected ? 'rgba(30, 214, 179, 0.1)' : 'rgba(255, 255, 255, 0.05)'
                          }}
                          onMouseEnter={(e) => {
                            if (!isSelected) e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)';
                          }}
                          onMouseLeave={(e) => {
                            if (!isSelected) e.currentTarget.style.background = 'rgba(255, 255, 255, 0.05)';
                          }}
                        >
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => handleOTAToggle(ota.id)}
                            className="ha-w-5 ha-h-5 ha-rounded ha-border-2 ha-border-primary ha-cursor-pointer"
                            style={{
                              background: isSelected ? 'hsl(var(--ha-primary))' : 'rgba(255, 255, 255, 0.1)',
                              accentColor: 'hsl(var(--ha-primary))'
                            }}
                          />
                          <span className="ha-text-black ha-text-sm">{ota.name}</span>
                        </label>
                      );
                    })}
                  </div>
                </div>

                {/* Optional URL */}
                <div>
                  <label className="ha-block ha-text-black ha-text-sm ha-font-semibold ha-mb-2">
                    URL Booking.com <span className="ha-text-gray-400 ha-font-normal">(optionnel)</span>
                  </label>
                  <input
                    type="url"
                    value={customUrl}
                    onChange={(e) => setCustomUrl(e.target.value)}
                    placeholder="https://www.booking.com/hotel/..."
                    className="ha-w-full ha-px-4 ha-py-3 ha-rounded-xl ha-border-2 ha-border-primary/50 ha-text-black ha-placeholder:text-gray-500 focus-visible:ha-outline-none focus-visible:ha-border-primary focus-visible:ha-bg-white/20 ha-transition-all ha-text-sm"
                    style={{
                      background: 'rgba(255, 255, 255, 0.1)',
                      backdropFilter: 'blur(10px)'
                    }}
                  />
                </div>
              </div>

              {/* CENTER COLUMN - GO Button */}
              <div className="ha-flex ha-justify-center ha-items-center ha-py-12">
                <button
                  onClick={handleAnalyze}
                  disabled={!canAnalyze || isAnalyzing}
                  className={`ha-relative ha-group ${
                    canAnalyze && !isAnalyzing ? 'ha-cursor-pointer' : 'ha-cursor-not-allowed'
                  }`}
                >
                  {/* Outer glow rings */}
                  {canAnalyze && (
                    <>
                      <div className={`ha-absolute ha-inset-0 ha-rounded-full ha-bg-primary ha-blur-2xl ha-opacity-50 ${isAnalyzing ? 'ha-animate-pulse' : 'group-hover:ha-opacity-75'} ha-transition-opacity`} />
                      {isAnalyzing && (
                        <>
                          <div className="ha-absolute ha-inset-0 ha-rounded-full ha-bg-primary ha-animate-ping ha-opacity-75" />
                          <div className="ha-absolute ha-inset-0 ha-rounded-full ha-bg-primary ha-animate-pulse" />
                        </>
                      )}
                    </>
                  )}
                  
                  {/* Main button */}
                  <div 
                    className={`ha-relative ha-w-48 ha-h-48 ha-rounded-full ha-flex ha-flex-col ha-items-center ha-justify-center ha-transition-all ha-duration-300 ${
                      isAnalyzing 
                        ? 'ha-scale-95' 
                        : canAnalyze 
                          ? 'group-hover:ha-scale-110 group-active:ha-scale-95' 
                          : ''
                    }`}
                    style={canAnalyze ? {
                      background: 'linear-gradient(135deg, #1ED6B3 0%, #1BC9A8 50%, #ffffff 100%)',
                      boxShadow: '0 0 60px rgba(30, 214, 179, 0.6)'
                    } : {
                      background: 'rgba(156, 163, 175, 0.3)'
                    }}
                  >
                    {/* Inner circle */}
                    <div 
                      className="ha-absolute ha-inset-3 ha-rounded-full ha-flex ha-flex-col ha-items-center ha-justify-center ha-p-10"
                      style={{
                        background: canAnalyze ? 'linear-gradient(135deg, #1ED6B3, #1BC9A8)' : '#1dd2b0'
                      }}
                    >
                      <div className="ha-text-center">
                        {isAnalyzing ? (
                          <>
                            {/* Ici vous pouvez ajouter un spinner ou texte d'attente */}
                            <div className="ha-text-white ha-font-bold ha-text-5xl ha-mb-2">...</div>
                            <div className="ha-text-white/80 ha-text-xs ha-uppercase ha-tracking-widest">Analyse...</div>
                          </>
                        ) : (
                          <>
                            <div className="ha-text-white ha-font-bold ha-text-5xl ha-mb-2">GO</div>
                            <div className="ha-text-white/80 ha-text-xs ha-uppercase ha-tracking-widest">Analyser</div>
                          </>
                        )}
                      </div>
                    </div>
                    
                    {/* Rotating border effect */}
                    {canAnalyze && !isAnalyzing && (
                      <div className="ha-absolute ha-inset-0 ha-rounded-full ha-border-4 ha-border-transparent group-hover:ha-border-white/20 ha-transition-all" />
                    )}
                  </div>
                </button>
              </div>


              

              {/* RIGHT COLUMN - Information */}
              <div className="ha-space-y-6">
                <h4 className="ha-text-black ha-text-xl ha-font-bold ha-mb-6 ha-text-center ha-lg:text-left">Informations</h4>
                
                {/* Info Cards */}
                <div 
                  className="ha-p-4 ha-rounded-xl ha-border ha-border-primary/30"
                  style={{
                    background: 'rgba(30, 214, 179, 0.1)'
                  }}
                >
                  <div className="ha-flex ha-items-start ha-gap-3">
                    <div className="ha-text-3xl">⚡</div>
                    <div>
                      <div className="ha-text-black ha-font-semibold ha-text-sm">Analyse Rapide</div>
                      <div className="ha-text-gray-400 ha-text-xs ha-mt-1">Résultats en moins de 30 secondes</div>
                    </div>
                  </div>
                </div>

                <div 
                  className="ha-p-4 ha-rounded-xl ha-border ha-border-green-500/30"
                  style={{
                    background: 'rgba(34, 197, 94, 0.1)'
                  }}
                >
                  <div className="ha-flex ha-items-start ha-gap-3">
                    <div className="ha-text-3xl">🔒</div>
                    <div>
                      <div className="ha-text-black ha-font-semibold ha-text-sm">100% Sécurisé</div>
                      <div className="ha-text-gray-400 ha-text-xs ha-mt-1">Vos données sont protégées</div>
                    </div>
                  </div>
                </div>

                <div 
                  className="ha-p-4 ha-rounded-xl ha-border ha-border-white/30"
                  style={{
                    background: 'rgba(255, 255, 255, 0.1)'
                  }}
                >
                  <div className="ha-flex ha-items-start ha-gap-3">
                    <div className="ha-text-3xl">✓</div>
                    <div>
                      <div className="ha-text-black ha-font-semibold ha-text-sm">Sans Engagement</div>
                      <div className="ha-text-gray-400 ha-text-xs ha-mt-1">Aucune inscription requise</div>
                    </div>
                  </div>
                </div>

                {/* Note */}
                <div 
                  className="ha-p-4 ha-rounded-xl ha-border ha-border-primary/20"
                  style={{
                    background: 'rgba(255, 255, 255, 0.05)'
                  }}
                >
                  <div className="ha-text-gray-400 ha-text-xs ha-leading-relaxed">
                    <strong className="ha-text-black">Note:</strong> L'analyse compare vos tarifs sur les OTA
                    sélectionnées avec votre prix officiel pour détecter les undercuts.
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};